/**
 * Privacy-preserving user-id hash for the analytics pipeline.
 *
 * Card LeZvSY6e — OBS F0.4 Consent module.
 *
 * Uses the Web Crypto API (`crypto.subtle.digest`) to compute a SHA-256
 * digest of the raw user id, hex-encodes it, and returns the first 32
 * characters (128 bits). That gives analytics enough entropy to
 * de-duplicate sessions per user without ever sending the raw Auth0 sub /
 * email / etc. downstream.
 *
 * Deterministic: the same input always yields the same output. This is
 * intentional — analytics needs joinability across events. If you need
 * unlinkable ids across days, prepend a rotating salt at the caller.
 *
 * Zero dependencies. Runs in modern browsers and in jsdom / node ≥ 20
 * (which exposes `globalThis.crypto.subtle`).
 */

/** Length of the truncated hex digest returned to callers. */
export const HASH_HEX_LENGTH = 32;

/**
 * Compute a stable, privacy-preserving id from `userId`.
 *
 * Throws when the Web Crypto API is unavailable (very old browsers, some
 * Web Workers with a restricted global). Callers should treat this as a
 * hard error rather than a fallback — hashing MUST succeed before any
 * user-id is forwarded to analytics.
 */
export async function hashUserId(userId: string): Promise<string> {
  if (typeof userId !== 'string' || userId.length === 0) {
    throw new Error('hashUserId: userId must be a non-empty string');
  }

  const subtle = getSubtleCrypto();
  if (!subtle) {
    throw new Error('hashUserId: Web Crypto API (subtle) is not available');
  }

  const bytes = new TextEncoder().encode(userId);
  const digest = await subtle.digest('SHA-256', bytes);
  return toHex(new Uint8Array(digest)).slice(0, HASH_HEX_LENGTH);
}

// -------------------------- internals -------------------------------------

function getSubtleCrypto(): SubtleCrypto | null {
  // Prefer globalThis so this works in browser main thread, workers, node,
  // and jsdom without a branch per environment.
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c?.subtle ?? null;
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}
